import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './styles/HomePage.css';
import RusMap from './components/LabList/Map';
import LoadingSpinner from './components/LoadingSpinner';
import { getApiV1Url } from './config/api';
import { useUser } from './contexts/UserContext';
import { getLegalBasis } from './utils/declarationUtils';

// Справочник регионов
const REGION_REFERENCE = [
  'Алтайский край', 'Амурская обл.', 'Архангельская обл.', 'Астраханская обл.', 'Белгородская обл.',
  'Брянская обл.', 'Владимирская обл.', 'Волгоградская обл.', 'Вологодская обл.', 'Еврейская автономная обл.',
  'Забайкальский край', 'Ивановская обл.', 'Иркутская обл.', 'Кабардино-Балкарская республика', 'Калининградская обл.',
  'Калужская обл.', 'Камчатский край', 'Карачаевая-Черкесская республика', 'Кемеровская обл.', 'Кировская обл.',
  'Костромская обл.', 'Краснодарский край', 'Красноярский край', 'Курганская обл.', 'Курская обл.',
  'Ленинградская обл.', 'Липецкая обл.', 'Магаданская обл.', 'Москва', 'Московская обл.',
  'Мурманская обл.', 'Ненецкий АО', 'Нижегородская обл.', 'Новгородская обл.', 'Новосибирская обл.',
  'Омская обл.', 'Оренбургская обл.', 'Орловская обл.', 'Пензенская обл.', 'Пермский край',
  'Приморский край', 'Республика Адыгея', 'Республика Алтай', 'Республика Башкортостан', 'Республика Бурятия',
  'Республика Дагестан', 'Республика Ингушетия', 'Республика Калмыкия', 'Республика Карелия', 'Республика Коми',
  'Республика Крым', 'Республика Марий-Эл', 'Республика Мордовия', 'Республика Саха(Якутия)', 'Республика Северная Осетия',
  'Республика Татарстан', 'Республика Тыва', 'Республика Хакасия', 'Ростовская обл.', 'Рязанская обл.',
  'Самарская обл.', 'Санкт-Петербург', 'Саратовская обл.', 'Сахалинская обл.', 'Свердловская обл.',
  'Севастополь', 'Смоленская обл.', 'Ставропольский край', 'Тамбовская обл.', 'Тверская обл.',
  'Томская обл.', 'Тульская обл.', 'Тюменская обл.', 'Республика Удмуртия', 'Ульяновская обл.',
  'Хабаровский край', 'Ханты-Мансийский АО', 'Челябинская обл.', 'Чеченская республика', 'Республика Чувашия',
  'Чукотский АО', 'Ямало-Ненецкий АО', 'Ярославская обл.',
  'Псковская обл.'
];

function normalizeRegion(str: string): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\b(область|обл|край|республика|город|г|ао|автономный округ|респ)\b/g, '')
    .replace(/\(|\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function HomePage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [certDropdown, setCertDropdown] = useState(false);
  const [polymerDropdown, setPolymerDropdown] = useState(false);
  const [labDropdown, setLabDropdown] = useState(false);
  const [rosakkreditatsiyaDropdown, setRosakkreditatsiyaDropdown] = useState(false);

  const [labExamCount, setLabExamCount] = useState<number | null>(null);
  const [mandatoryCertCount, setMandatoryCertCount] = useState<number | null>(null);
  const [voluntaryCertCount, setVoluntaryCertCount] = useState<number | null>(null);
  const [declaration2425Count, setDeclaration2425Count] = useState<number | null>(null);
  const [declaration353Count, setDeclaration353Count] = useState<number | null>(null);
  const [uniqueManufacturers, setUniqueManufacturers] = useState<number | null>(null);
  const [uniqueRegions, setUniqueRegions] = useState<number | null>(null);
  const [labExamData, setLabExamData] = useState<any[]>([]);
  const [bitrixAppealsCount, setBitrixAppealsCount] = useState<number | null>(null);
  const [lettersCount, setLettersCount] = useState<number | null>(null);

  const isEditor = !!user?.groups?.some((g) => g.name === 'Редактор');

  const PE_KEYS = [
    'plast02651', 'HD03490NP', 'HD03594PE', 'HM0349PE', 'HM0359PE', 'PE6PP32',
    'p2nt119', 'HD12500LB', 'stavrolen27383', 'HD07580SB', 'plast27673',
    'HD03580SB', 'HD10530LB', 'PE100RC'
  ];
  const PPR_KEYS = [
    'PPI013GP5', 'PPG200224', 'PPH250GP2', 'plast1030', 'PPG103508',
    'PPH030GPTomskneftehim', 'PPH030GPNeftehimia', 'PPH030GPPolium',
    'PPH030GPZapSibNeftehim', 'SIBEX_PP_R003_EX1'
  ];
  const PVH_KEYS = [
    'C70Y', 'PC271', 'SI70', 'C5868PG', 'C6359M', 'C6669PG', 'RC267',
    'C6669GC', 'SI67', 'C7059M'
  ];
  const polymerTotal = PE_KEYS.length + PPR_KEYS.length + PVH_KEYS.length;

  useEffect(() => {
    const access = localStorage.getItem('access');
    axios.get(getApiV1Url('LAB_EXAMS_LIST'), {
      headers: access ? { Authorization: `Bearer ${access}` } : {}
    })
      .then(res => {
        if (Array.isArray(res.data)) {
          setLabExamData(res.data);
          setLabExamCount(res.data.length);
          const seen = new Set();
          let count = 0;
          for (const item of res.data) {
            if (item.inn_company && !seen.has(item.inn_company)) {
              seen.add(item.inn_company);
              count++;
            }
          }
          setUniqueManufacturers(count);
          const regionSet = new Set();
          for (const item of res.data) {
            if (!item.region_company) continue;
            const norm = normalizeRegion(item.region_company);
            for (const ref of REGION_REFERENCE) {
              if (normalizeRegion(ref) === norm) {
                regionSet.add(ref);
                break;
              }
            }
          }
          setUniqueRegions(regionSet.size);
        } else {
          setLabExamCount(0);
          setUniqueManufacturers(0);
          setUniqueRegions(0);
        }
      })
      .catch(() => {
        setLabExamCount(null);
        setUniqueManufacturers(null);
        setUniqueRegions(null);
      });
    axios.get(getApiV1Url('CERTIFICATES_COUNT'), {
      headers: access ? { Authorization: `Bearer ${access}` } : {}
    })
      .then(response => setMandatoryCertCount(response.data.count || 0))
      .catch(() => setMandatoryCertCount(null));
    axios.get(getApiV1Url('VOLUNTARY_CERTIFICATES'), {
      headers: access ? { Authorization: `Bearer ${access}` } : {}
    })
      .then(res => setVoluntaryCertCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => setVoluntaryCertCount(null));
    // Только количество по основаниям, без загрузки списков деклараций
    axios.get(getApiV1Url('DECLARATIONS_COUNT'), {
      headers: access ? { Authorization: `Bearer ${access}` } : {}
    })
      .then(response => {
        const declarationsCountByBasis = response.data;
        setDeclaration2425Count(declarationsCountByBasis[getLegalBasis('2425')] || 0);
        setDeclaration353Count(declarationsCountByBasis[getLegalBasis('353')] || 0);
      })
      .catch(() => {
        setDeclaration2425Count(null);
        setDeclaration353Count(null);
      });
    axios.get(getApiV1Url('BITRIX_DEALS'), {
      headers: access ? { Authorization: `Bearer ${access}` } : {}
    })
      .then(res => setBitrixAppealsCount(res.data.total || 0))
      .catch(() => setBitrixAppealsCount(null));
    axios.get(getApiV1Url('LETTERS_COUNT'), {
      headers: access ? { Authorization: `Bearer ${access}` } : {}
    })
      .then(response => setLettersCount(response.data.count || 0))
      .catch(() => setLettersCount(null));
  }, []);

  const numberStyle = { fontSize: 72, fontWeight: 700, lineHeight: 1, margin: 0 };
  const textStyle = { fontSize: 18, fontWeight: 400, margin: 0 };
  const totalCertificatesCount = (mandatoryCertCount !== null && voluntaryCertCount !== null)
    ? mandatoryCertCount + voluntaryCertCount
    : null;

  return (
    <div className="home-redesign-container">
      <div className="main-left-column">
        <div className="home-map-area">
          <RusMap className="rusmap-svg" examData={labExamData} />
        </div>
        <div className="data-blocks-area">
          <div className="map-block-row">
            <div className="map-block">
              <h1 style={numberStyle}>{labExamCount !== null ? labExamCount : <LoadingSpinner />}</h1>
              <p style={textStyle}>лабораторных испытаний</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{totalCertificatesCount !== null ? totalCertificatesCount : <LoadingSpinner />}</h1>
              <p style={textStyle}>проверенных сертификатов</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{mandatoryCertCount !== null ? mandatoryCertCount : <LoadingSpinner />}</h1>
              <p style={textStyle}>обязательных сертификатов</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{declaration2425Count !== null ? declaration2425Count : <LoadingSpinner />}</h1>
              <p style={textStyle}>деклараций ПП 2425</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{declaration353Count !== null ? declaration353Count : <LoadingSpinner />}</h1>
              <p style={textStyle}>деклараций ПП 353</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{voluntaryCertCount !== null ? voluntaryCertCount : <LoadingSpinner />}</h1>
              <p style={textStyle}>добровольных сертификатов</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{polymerTotal}</h1>
              <p style={textStyle}>марки полимеров</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{uniqueManufacturers !== null ? uniqueManufacturers : <LoadingSpinner />}</h1>
              <p style={textStyle}>проверенных производителей</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{bitrixAppealsCount !== null ? bitrixAppealsCount : <LoadingSpinner />}</h1>
              <p style={textStyle}>обращений в АПТС</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{lettersCount !== null ? lettersCount : <LoadingSpinner />}</h1>
              <p style={textStyle}>официальных писем АПТС</p>
            </div>
            <div className="map-block">
              <h1 style={numberStyle}>{uniqueRegions !== null ? uniqueRegions : <LoadingSpinner />}</h1>
              <p style={textStyle}>субъектов РФ в которых производилась проверка АПТС</p>
            </div>
          </div>
        </div>
      </div>
      <div className="home-nav-area">
        <nav className="main-nav-menu">
          <div className="nav-item nav-dropdown">
            <div className="nav-link" onClick={() => { setPolymerDropdown(false); setLabDropdown(false); setRosakkreditatsiyaDropdown(false); setCertDropdown(v => !v); }}>
              Сертификаты и декларации
              <span className="dropdown-arrow"> <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 16.8C11.3 16.8 10.6 16.53 10.07 16L3.55002 9.48001C3.26002 9.19001 3.26002 8.71001 3.55002 8.42001C3.84002 8.13001 4.32002 8.13001 4.61002 8.42001L11.13 14.94C11.61 15.42 12.39 15.42 12.87 14.94L19.39 8.42001C19.68 8.13001 20.16 8.13001 20.45 8.42001C20.74 8.71001 20.74 9.19001 20.45 9.48001L13.93 16C13.4 16.53 12.7 16.8 12 16.8Z" fill="currentColor" />
          </svg>
          </span>
            </div>
            {certDropdown && (
              <div className="dropdown-menu">
                <div className="dropdown-link" onClick={() => navigate('/certificates')}>Обязательные сертификаты</div>
                <div className="dropdown-link" onClick={() => navigate('/voluntary-certificates')}>Добровольные сертификаты</div>
                <div className="dropdown-link" onClick={() => navigate('/declarations/2425')}>Декларации ПП 2425</div>
                <div className="dropdown-link" onClick={() => navigate('/declarations/353')}>Декларации ПП 353</div>
                {isEditor && (
                  <div className="dropdown-link" onClick={() => navigate('/voluntary-add')}>Форма добавления</div>
                )}
              </div>
            )}
          </div>
          <div className="nav-item">
            <div className="nav-link" onClick={() => navigate('/org-form')}>Комплексная проверка организации</div>
          </div>
          <div className={`nav-item nav-dropdown ${labDropdown ? 'open' : ''}`}>
            <div className="nav-link" onClick={() => { setCertDropdown(false); setPolymerDropdown(false); setRosakkreditatsiyaDropdown(false); setLabDropdown(v => !v); }}>
              Лабораторные испытания
              <span className="dropdown-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 16.8C11.3 16.8 10.6 16.53 10.07 16L3.55002 9.48001C3.26002 9.19001 3.26002 8.71001 3.55002 8.42001C3.84002 8.13001 4.32002 8.13001 4.61002 8.42001L11.13 14.94C11.61 15.42 12.39 15.42 12.87 14.94L19.39 8.42001C19.68 8.13001 20.16 8.13001 20.45 8.42001C20.74 8.71001 20.74 9.19001 20.45 9.48001L13.93 16C13.4 16.53 12.7 16.8 12 16.8Z" fill="currentColor" />
          </svg>
          </span>
            </div>
            {labDropdown && (
              <div className="dropdown-menu">
                <div className="dropdown-link" onClick={() => navigate('/lab-exam')}>Реестр лабораторных испытаний</div>
                <div className="dropdown-link" onClick={() => navigate('/welded-joint-exams')}>Реестр испытаний сварных соединений</div>
                <div className="dropdown-link" onClick={() => navigate('lab-exam/deleted')}>Удаленные испытания</div>
                {isEditor && (
                  <div className="dropdown-link" onClick={() => navigate('/lab-add')}>Форма добавления</div>
                )}
              </div>
            )}
          </div>
          <div className="nav-item nav-dropdown">
            <div className="nav-link" onClick={() => { setCertDropdown(false); setLabDropdown(false); setRosakkreditatsiyaDropdown(false); setPolymerDropdown(v => !v); }}>
              Цены на полимерное сырье
              <span className="dropdown-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 16.8C11.3 16.8 10.6 16.53 10.07 16L3.55002 9.48001C3.26002 9.19001 3.26002 8.71001 3.55002 8.42001C3.84002 8.13001 4.32002 8.13001 4.61002 8.42001L11.13 14.94C11.61 15.42 12.39 15.42 12.87 14.94L19.39 8.42001C19.68 8.13001 20.16 8.13001 20.45 8.42001C20.74 8.71001 20.74 9.19001 20.45 9.48001L13.93 16C13.4 16.53 12.7 16.8 12 16.8Z" fill="currentColor" />
          </svg>
          </span>
            </div>
            {polymerDropdown && (
              <div className="dropdown-menu">
                <div className="dropdown-link" onClick={() => navigate('/material?type=pe')}>Полиэтилен</div>
                <div className="dropdown-link" onClick={() => navigate('/material?type=ppr')}>Полипропилен</div>
                <div className="dropdown-link" onClick={() => navigate('/material?type=pvh')}>Поливинилхлорид</div>
                {isEditor && (
                  <div className="dropdown-link" onClick={() => navigate('/material-price')}>Форма добавления</div>
                )}
              </div>
            )}
          </div>
          <div className={`nav-item nav-dropdown ${rosakkreditatsiyaDropdown ? 'open' : ''}`}>
            <div className="nav-link" onClick={() => { setCertDropdown(false); setPolymerDropdown(false); setLabDropdown(false); setRosakkreditatsiyaDropdown(v => !v); }}>
              Росаккредитация
              <span className="dropdown-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 16.8C11.3 16.8 10.6 16.53 10.07 16L3.55002 9.48001C3.26002 9.19001 3.26002 8.71001 3.55002 8.42001C3.84002 8.13001 4.32002 8.13001 4.61002 8.42001L11.13 14.94C11.61 15.42 12.39 15.42 12.87 14.94L19.39 8.42001C19.68 8.13001 20.16 8.13001 20.45 8.42001C20.74 8.71001 20.74 9.19001 20.45 9.48001L13.93 16C13.4 16.53 12.7 16.8 12 16.8Z" fill="currentColor" />
          </svg>
          </span>
            </div>
            {rosakkreditatsiyaDropdown && (
              <div className="dropdown-menu">
                <div className="dropdown-link" onClick={() => navigate('/rosakkreditatsiya/os')}>ОС</div>
                <div className="dropdown-link" onClick={() => navigate('/rosakkreditatsiya/il')}>ИЛ</div>
              </div>
            )}
          </div>
          <div className="nav-item">
            <div className="nav-link" onClick={() => navigate('/letters')}>Работа с письмами</div>
          </div>
        </nav>
      </div>
    </div>
  );
}

export default HomePage;
